// components/ChatbotAdmin/ChatbotAdminPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  Switch, FormControlLabel, Select, MenuItem, FormControl,
  InputLabel, Tabs, Tab, Tooltip, CircularProgress, Alert,
  Snackbar, Checkbox, ListItemText, OutlinedInput, Divider,
  Card, CardContent, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  HelpOutline as IssueIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL } from '../services/configUrls';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

const ROLE_OPTIONS = [
  { value: 'spoc',     label: 'SPOC' },
  { value: 'd_spoc',   label: 'D-SPOC' },
  { value: 'leader',   label: 'Leader' },
  { value: 'tpo',      label: 'TPO' },
  { value: 'educator', label: 'Educator' },
];

const ROLE_COLORS = {
  spoc:     '#1976d2',
  d_spoc:   '#7b1fa2',
  leader:   '#388e3c',
  tpo:      '#f57c00',
  educator: '#c62828',
};

// ── Snackbar helper ──────────────────────────────────────────
const useSnack = () => {
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const show = (msg, severity = 'success') => setSnack({ open: true, msg, severity });
  const hide = () => setSnack(s => ({ ...s, open: false }));
  return { snack, show, hide };
};

// ─────────────────────────────────────────────────────────────
//  CATEGORY DIALOG
// ─────────────────────────────────────────────────────────────
const CategoryDialog = ({ open, onClose, onSave, initial }) => {
  const [name, setName]       = useState('');
  const [active, setActive]   = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.category_name || '');
      setActive(initial ? initial.is_active === 1 : true);
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setLoading(true);
    await onSave({ category_name: name.trim(), is_active: active ? 1 : 0 });
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ fontWeight: 700, color: '#0288D1' }}>
        {initial ? 'Edit Category' : 'Add Category'}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth label="Category Name" value={name}
          onChange={e => setName(e.target.value)}
          sx={{ mt: 1 }} autoFocus
        />
        <FormControlLabel
          control={<Switch checked={active} onChange={e => setActive(e.target.checked)} color="primary" />}
          label="Active"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || !name.trim()}
          sx={{ bgcolor: '#0288D1', '&:hover': { bgcolor: '#0277BD' } }}>
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
//  ISSUE DIALOG
// ─────────────────────────────────────────────────────────────
const IssueDialog = ({ open, onClose, onSave, initial, categories }) => {
  const [form, setForm] = useState({
    category_id: '', issue: '', solution: '',
    display_order: '', is_active: 1, roles: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        category_id:   initial?.category_id   || '',
        issue:         initial?.issue         || '',
        solution:      initial?.solution      || '',
        display_order: initial?.display_order ?? '',
        is_active:     initial?.is_active     ?? 1,
        roles:         initial?.roles         || [],
      });
    }
  }, [open, initial]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.category_id || !form.issue.trim()) return;
    setLoading(true);
    await onSave({
      ...form,
      display_order: form.display_order === '' ? null : Number(form.display_order),
    });
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ fontWeight: 700, color: '#0288D1' }}>
        {initial ? 'Edit Issue' : 'Add Issue'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Category */}
          <FormControl fullWidth>
            <InputLabel>Category *</InputLabel>
            <Select value={form.category_id} label="Category *"
              onChange={e => set('category_id', e.target.value)}>
              {categories.map(c => (
                <MenuItem key={c.category_id} value={c.category_id}>{c.category_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Issue */}
          <TextField fullWidth label="Issue / Question *" value={form.issue}
            onChange={e => set('issue', e.target.value)} />

          {/* Solution */}
          <TextField fullWidth label="Solution" value={form.solution}
            onChange={e => set('solution', e.target.value)}
            multiline rows={4} />

          {/* Display Order */}
          <TextField fullWidth label="Display Order" value={form.display_order}
            onChange={e => set('display_order', e.target.value)}
            type="number" helperText="Leave blank for auto-order" />

          {/* Roles */}
          <FormControl fullWidth>
            <InputLabel>Visible to Roles</InputLabel>
            <Select
              multiple value={form.roles}
              onChange={e => set('roles', e.target.value)}
              input={<OutlinedInput label="Visible to Roles" />}
              renderValue={selected =>
                selected.length === 0
                  ? <em style={{ color: '#888' }}>All roles (no restriction)</em>
                  : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(r => (
                        <Chip key={r} label={ROLE_OPTIONS.find(o => o.value === r)?.label || r}
                          size="small"
                          sx={{ bgcolor: ROLE_COLORS[r] + '22', color: ROLE_COLORS[r], fontWeight: 600 }} />
                      ))}
                    </Box>
                  )
              }
            >
              {ROLE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox checked={form.roles.includes(opt.value)} />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
              Leave empty = visible to ALL roles
            </Typography>
          </FormControl>

          {/* Active toggle */}
          <FormControlLabel
            control={
              <Switch checked={form.is_active === 1}
                onChange={e => set('is_active', e.target.checked ? 1 : 0)} color="primary" />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained"
          disabled={loading || !form.category_id || !form.issue.trim()}
          sx={{ bgcolor: '#0288D1', '&:hover': { bgcolor: '#0277BD' } }}>
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
//  DELETE CONFIRM DIALOG
// ─────────────────────────────────────────────────────────────
const DeleteDialog = ({ open, onClose, onConfirm, label }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
    PaperProps={{ sx: { borderRadius: '12px' } }}>
    <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f' }}>Confirm Delete</DialogTitle>
    <DialogContent>
      <Typography>Are you sure you want to delete <strong>"{label}"</strong>? This cannot be undone.</Typography>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} variant="contained" color="error">Delete</Button>
    </DialogActions>
  </Dialog>
);

// ─────────────────────────────────────────────────────────────
//  CATEGORIES TAB
// ─────────────────────────────────────────────────────────────
const CategoriesTab = ({ categories, loading, onReload, showSnack }) => {
  const [dlgOpen, setDlgOpen]     = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [delDlg, setDelDlg]       = useState(null);

  const handleSave = async (payload) => {
    try {
      if (editItem) {
        await axios.put(`${BASE_URL}/admin_chatbot_crud/categories/${editItem.category_id}`,
          payload, { headers: authHeaders() });
        showSnack('Category updated!');
      } else {
        await axios.post(`${BASE_URL}/admin_chatbot_crud/categories`,
          payload, { headers: authHeaders() });
        showSnack('Category created!');
      }
      setDlgOpen(false);
      setEditItem(null);
      onReload();
    } catch (err) {
      showSnack(err?.response?.data?.detail || 'Error saving category.', 'error');
    }
  };

  const handleToggle = async (cat) => {
    try {
      await axios.patch(`${BASE_URL}/admin_chatbot_crud/categories/${cat.category_id}/toggle`,
        {}, { headers: authHeaders() });
      showSnack(`Category ${cat.is_active === 1 ? 'deactivated' : 'activated'}!`);
      onReload();
    } catch {
      showSnack('Toggle failed.', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/admin_chatbot_crud/categories/${delDlg.category_id}`,
        { headers: authHeaders() });
      showSnack('Category deleted!');
      setDelDlg(null);
      onReload();
    } catch (err) {
      showSnack(err?.response?.data?.detail || 'Delete failed.', 'error');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>Categories ({categories.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setEditItem(null); setDlgOpen(true); }}
          sx={{ bgcolor: '#0288D1', '&:hover': { bgcolor: '#0277BD' }, borderRadius: '8px' }}>
          Add Category
        </Button>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Card sx={{ flex: 1, borderRadius: '10px', border: '1px solid #e0f2fe' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h5" fontWeight={700} color="#0288D1">{categories.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, borderRadius: '10px', border: '1px solid #e8f5e9' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Active</Typography>
            <Typography variant="h5" fontWeight={700} color="#388e3c">
              {categories.filter(c => c.is_active === 1).length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, borderRadius: '10px', border: '1px solid #fff3e0' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Inactive</Typography>
            <Typography variant="h5" fontWeight={700} color="#f57c00">
              {categories.filter(c => c.is_active === 0).length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#0288D1' }} />
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#888' }}>
                  No categories yet. Add one!
                </TableCell>
              </TableRow>
            ) : (
              categories.map((cat, idx) => (
                <TableRow key={cat.category_id} hover>
                  <TableCell sx={{ color: '#888', fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{cat.category_name}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={cat.is_active === 1 ? 'Click to deactivate' : 'Click to activate'}>
                      <Chip
                        label={cat.is_active === 1 ? 'Active' : 'Inactive'}
                        size="small"
                        onClick={() => handleToggle(cat)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: cat.is_active === 1 ? '#e8f5e9' : '#ffebee',
                          color:   cat.is_active === 1 ? '#388e3c' : '#c62828',
                          fontWeight: 600,
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditItem(cat); setDlgOpen(true); }}
                        sx={{ color: '#0288D1' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDelDlg(cat)}
                        sx={{ color: '#d32f2f' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CategoryDialog open={dlgOpen} onClose={() => { setDlgOpen(false); setEditItem(null); }}
        onSave={handleSave} initial={editItem} />
      <DeleteDialog open={!!delDlg} onClose={() => setDelDlg(null)}
        onConfirm={handleDelete} label={delDlg?.category_name} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
//  ISSUES TAB
// ─────────────────────────────────────────────────────────────
const IssuesTab = ({ categories, showSnack }) => {
  const [issues, setIssues]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [dlgOpen, setDlgOpen]     = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [delDlg, setDelDlg]       = useState(null);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterCat
        ? `${BASE_URL}/admin_chatbot_crud/issues?category_id=${filterCat}`
        : `${BASE_URL}/admin_chatbot_crud/issues`;
      const res = await axios.get(url, { headers: authHeaders() });
      setIssues(res.data || []);
    } catch {
      showSnack('Failed to load issues.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterCat]);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  const getCategoryName = (id) =>
    categories.find(c => c.category_id === id)?.category_name || `Cat #${id}`;

  const handleSave = async (payload) => {
    try {
      if (editItem) {
        await axios.put(`${BASE_URL}/admin_chatbot_crud/issues/${editItem.issue_id}`,
          payload, { headers: authHeaders() });
        showSnack('Issue updated!');
      } else {
        await axios.post(`${BASE_URL}/admin_chatbot_crud/issues`,
          payload, { headers: authHeaders() });
        showSnack('Issue created!');
      }
      setDlgOpen(false);
      setEditItem(null);
      loadIssues();
    } catch (err) {
      showSnack(err?.response?.data?.detail || 'Error saving issue.', 'error');
    }
  };

  const handleToggle = async (issue) => {
    try {
      await axios.patch(`${BASE_URL}/admin_chatbot_crud/issues/${issue.issue_id}/toggle`,
        {}, { headers: authHeaders() });
      showSnack(`Issue ${issue.is_active === 1 ? 'deactivated' : 'activated'}!`);
      loadIssues();
    } catch {
      showSnack('Toggle failed.', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/admin_chatbot_crud/issues/${delDlg.issue_id}`,
        { headers: authHeaders() });
      showSnack('Issue deleted!');
      setDelDlg(null);
      loadIssues();
    } catch (err) {
      showSnack(err?.response?.data?.detail || 'Delete failed.', 'error');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" fontWeight={700}>Issues ({issues.length})</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Filter by category */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select value={filterCat} label="Filter by Category"
              onChange={e => setFilterCat(e.target.value)}
              sx={{ borderRadius: '8px' }}>
              <MenuItem value="">All Categories</MenuItem>
              {categories.map(c => (
                <MenuItem key={c.category_id} value={c.category_id}>{c.category_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={loadIssues} sx={{ color: '#0288D1' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setEditItem(null); setDlgOpen(true); }}
            sx={{ bgcolor: '#0288D1', '&:hover': { bgcolor: '#0277BD' }, borderRadius: '8px' }}>
            Add Issue
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Issue</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Solution</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Roles</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Order</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} sx={{ color: '#0288D1' }} />
                </TableCell>
              </TableRow>
            ) : issues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#888' }}>
                  No issues found.
                </TableCell>
              </TableRow>
            ) : (
              issues.map((issue, idx) => (
                <TableRow key={issue.issue_id} hover>
                  <TableCell sx={{ color: '#888', fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                  <TableCell>
                    <Chip label={getCategoryName(issue.category_id)} size="small"
                      sx={{ bgcolor: '#e3f2fd', color: '#0288D1', fontWeight: 600, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={issue.issue}>
                      <Typography variant="body2" sx={{
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', maxWidth: 200, fontWeight: 500,
                      }}>
                        {issue.issue}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Tooltip title={issue.solution || 'No solution'}>
                      <Typography variant="body2" color="text.secondary" sx={{
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', maxWidth: 180, fontSize: '0.75rem',
                      }}>
                        {issue.solution || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {issue.roles.length === 0 ? (
                      <Chip label="All" size="small"
                        sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600, fontSize: '0.65rem' }} />
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, justifyContent: 'center' }}>
                        {issue.roles.map(r => (
                          <Chip key={r}
                            label={ROLE_OPTIONS.find(o => o.value === r)?.label || r}
                            size="small"
                            sx={{
                              bgcolor: (ROLE_COLORS[r] || '#888') + '22',
                              color:   ROLE_COLORS[r] || '#888',
                              fontWeight: 600, fontSize: '0.6rem',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {issue.display_order ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={issue.is_active === 1 ? 'Click to deactivate' : 'Click to activate'}>
                      <Chip
                        label={issue.is_active === 1 ? 'Active' : 'Inactive'}
                        size="small"
                        onClick={() => handleToggle(issue)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: issue.is_active === 1 ? '#e8f5e9' : '#ffebee',
                          color:   issue.is_active === 1 ? '#388e3c' : '#c62828',
                          fontWeight: 600,
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditItem(issue); setDlgOpen(true); }}
                        sx={{ color: '#0288D1' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDelDlg(issue)}
                        sx={{ color: '#d32f2f' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <IssueDialog open={dlgOpen} onClose={() => { setDlgOpen(false); setEditItem(null); }}
        onSave={handleSave} initial={editItem} categories={categories} />
      <DeleteDialog open={!!delDlg} onClose={() => setDelDlg(null)}
        onConfirm={handleDelete} label={delDlg?.issue} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
//  MAIN PANEL
// ─────────────────────────────────────────────────────────────
const ChatbotAdminPanel = () => {
  const [tab, setTab]             = useState(0);
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const { snack, show: showSnack, hide: hideSnack } = useSnack();

  const loadCategories = useCallback(async () => {
    setLoadingCats(true);
    try {
      const res = await axios.get(`${BASE_URL}/admin_chatbot_crud/categories`, {
        headers: authHeaders(),
      });
      setCategories(res.data || []);
    } catch {
      showSnack('Failed to load categories.', 'error');
    } finally {
      setLoadingCats(false);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="#0288D1">
          🤖 Chatbot Content Manager
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage categories, issues, solutions and role visibility for the Edi chatbot.
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' },
          '& .Mui-selected': { color: '#0288D1' },
          '& .MuiTabs-indicator': { bgcolor: '#0288D1' },
        }}>
        <Tab icon={<CategoryIcon />} iconPosition="start" label="Categories" />
        <Tab icon={<IssueIcon />}    iconPosition="start" label="Issues & Solutions" />
      </Tabs>

      {/* Tab Panels */}
      {tab === 0 && (
        <CategoriesTab
          categories={categories}
          loading={loadingCats}
          onReload={loadCategories}
          showSnack={showSnack}
        />
      )}
      {tab === 1 && (
        <IssuesTab
          categories={categories}
          showSnack={showSnack}
        />
      )}

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={hideSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={hideSnack} severity={snack.severity} sx={{ borderRadius: '8px' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatbotAdminPanel;